import DoneIcon from '@mui/icons-material/Done'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Fade, Grid, Stack, Typography } from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FieldModel } from '@cibo/core'
import { useFields, usePaginatedFields } from '@cibo/landmanager'
import { usePrograms } from '@cibo/programs'
import { LandThumbnail, useInterval } from '@cibo/ui'

const MIN_WAIT_TIME = 3000
const THUMBNAIL_SIZE = 60

type Props = {
  fieldIds: string[]
  onCompleteProfile(fieldIds: string[]): void
  onCreateEnrollment(fieldIds: string[]): void
  onSkipDetails(): void
  created?: boolean
}

export const AwaitDetails = ({
  fieldIds,
  onCreateEnrollment,
  onCompleteProfile,
  onSkipDetails,
  created = false,
}: Props) => {
  const { t } = useTranslation('awaitDetails')
  const fields = useFields(fieldIds)
  const [waitTime, setWaitTime] = useState<number>()
  const [startedWaiting, setStartedWaiting] = useState<number>()
  const [progress, setProgress] = useState<number>()
  const programs = usePrograms({ organization: true })
  const profileNeeded = usePaginatedFields(
    0,
    fieldIds.length,
    {
      resourceIds: fieldIds.join(','),
      anyPrograms: !!programs.data
        ? programs.data?.map(({ programId }) => ({
            id: programId,
            benchmarks: [{ benchmark: 'profile', status: 'incomplete' }],
          }))
        : undefined,
      includeAllDetails: true,
    },
    { enabled: !programs.isLoading }
  )
  const footprintsResolved = fields?.data?.every((field: FieldModel) => field.footprintResolved)

  const ciboDetails = fields?.data?.reduce(
    (acc: number, item: FieldModel) => acc + item.details.length,
    0
  )

  const tick = () => {
    const now = new Date().getTime()

    if (startedWaiting && waitTime) {
      const waitProgress = now - startedWaiting

      if (waitProgress < waitTime) {
        const newProgress = (waitProgress / waitTime) * 100
        setProgress(newProgress)
      } else {
        // @ts-ignore
        fields.refetch()
        setStartedWaiting(0)
        setProgress(0)
      }
    }
  }

  useInterval(tick, 500)

  useEffect(() => {
    if (fields.isFetching) {
      return
    }

    const now = new Date()

    const expectedCalcDate = fields?.data
      ?.map((field: FieldModel) => field.expectedCalcDate)
      .reduce((currentMax?: Date | '', item?: Date | '') => {
        if (!item) {
          return currentMax
        }
        if (!currentMax) {
          return item
        }

        return item.valueOf() > currentMax.valueOf() ? item : currentMax
      }, undefined)

    if (footprintsResolved === false && expectedCalcDate) {
      setStartedWaiting(now.valueOf())
      setWaitTime(Math.max(MIN_WAIT_TIME, expectedCalcDate.valueOf() - now.valueOf()))
      setProgress(0)
    } else {
      setStartedWaiting(0)
    }
  }, [fields.data, fields.isFetching, footprintsResolved])

  const dataIsLoading = !footprintsResolved

  return (
    <Stack spacing={2} p={2} justifyContent="center" alignItems="center">
      {fields?.data && (
        <Stack alignSelf="stretch" spacing={1}>
          {fields?.data.length === 1 ? (
            <Box alignSelf="center" borderRadius={1} overflow="hidden">
              <LandThumbnail landProfile={fields?.data[0]} height={300} width={300} />
            </Box>
          ) : (
            <Box>
              <Grid justifyContent="center" spacing={1} container>
                {fields.data.slice(0, 11).map(f => (
                  <Grid key={f.resourceId} item xs={2}>
                    <Box borderRadius={1} width={THUMBNAIL_SIZE}>
                      <LandThumbnail
                        landProfile={f}
                        height={THUMBNAIL_SIZE}
                        width={THUMBNAIL_SIZE}
                      />
                    </Box>
                  </Grid>
                ))}
                {fields.data.length === 12 ? (
                  <Grid container justifyContent="center" alignItems="center" item xs={2}>
                    <Box borderRadius={1} overflow="hidden" width={THUMBNAIL_SIZE}>
                      <LandThumbnail
                        landProfile={fields.data[11]}
                        height={THUMBNAIL_SIZE}
                        width={THUMBNAIL_SIZE}
                      />
                    </Box>
                  </Grid>
                ) : fields.data.length > 12 ? (
                  <Grid container justifyContent="center" alignItems="center" item xs={2}>
                    <Typography>{t('plusMore', { count: fields.data.length - 11 })}</Typography>
                  </Grid>
                ) : (
                  <></>
                )}
              </Grid>
            </Box>
          )}
          <Fade in={!footprintsResolved}>
            <LinearProgress sx={{ width: '100%' }} value={progress} />
          </Fade>
        </Stack>
      )}
      {!footprintsResolved ? (
        <Stack>
          <Typography>{t('body', { count: fieldIds.length })}</Typography>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1}>
          <Typography data-testid="remote_sensed">
            {t('ciboSensed', { count: ciboDetails })}
          </Typography>
          <DoneIcon color="success" />
        </Stack>
      )}
      <Stack direction="row" spacing={2} alignItems="flex-end">
        <Button
          data-testid="verify_prequalify"
          onClick={onSkipDetails}
          color="secondary"
          fullWidth={false}
        >
          {t('skip')}
        </Button>
        <LoadingButton
          data-testid="verify_prequalify"
          loading={dataIsLoading}
          onClick={() => {
            profileNeeded.data?.items.length === 0
              ? onCreateEnrollment(fieldIds)
              : onCompleteProfile(
                  profileNeeded.data?.items.map(({ resourceId }) => resourceId) || []
                )
          }}
          variant="contained"
        >
          {dataIsLoading
            ? t('nextSteps')
            : profileNeeded.data?.items.length === 0
            ? t('seeOpportunities')
            : t('addInformation', { count: profileNeeded.data?.numAvailable })}
        </LoadingButton>
      </Stack>
    </Stack>
  )
}
